/**
 * Demo workspace.
 *
 * Builds a real contract `UserWorkspace` (via the model package + mintUlid) so
 * the render path exercises the actual contract → chart-engine pipeline, not a
 * shortcut. A price pane (candles + two EMAs) over a volume pane, in a 1×2
 * layout beside a screener placeholder. Bar data is supplied at runtime by the
 * transport layer (a FixtureTransportClient offline; the live WS client later).
 */

import {
  mintUlid,
  validateWorkspace,
  QUFOLIO_CLASSIC_THEME,
  type UserWorkspace,
  type ScreenerConfig,
} from "@qufolio/model";
import { LOGO_COLUMN } from "@qufolio/screener-grid";
// The same factory the layout templates use. One definition of "a chart", so a
// chart the demo ships and a chart "2 across" creates cannot drift apart — they
// would differ in their output aliases, and the second would render nothing
// while looking correct.
import { newChartGroup } from "./chart-group";

/**
 * The demo screen: S&P 500 over the JAS Dots screen.
 *
 * A column view is supplied deliberately. The screen result carries ~123
 * columns and the server sends all of them, so with no view every one would
 * render — correct, but unusable as a first impression. The unlisted fields
 * stay available to switch on from the column menu.
 */
function screenerConfig(): ScreenerConfig {
  return {
    id: mintUlid(),
    group: "SP500",
    screen: "JASDOTS",
    columns: [
      LOGO_COLUMN,
      { field: "symbol", header: "Symbol", pinned: "left", width: 90 },
      { field: "name", header: "Name", width: 190 },
      { field: "sector", header: "Sector", width: 150 },
      { field: "close", header: "Last", precision: 2, width: 90 },
      {
        field: "changePercent",
        header: "Chg %",
        precision: 2,
        width: 90,
        // Semantic tokens, resolved to CSS at render time (D13). `pos`/`neg`
        // are the *same* tokens the price pane's candles use — one namespace,
        // so a positive cell and an up candle are the same green by
        // construction. (These previously named `chart.up`/`chart.down`, which
        // no theme defined; they resolved through a hardcoded map in the view.)
        format: [
          { when: { op: "gte", value: 0 }, style: { fg: "positive" } },
          { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
        ],
      },
      { field: "rvol", header: "RVol", precision: 2, width: 85 },
      { field: "rs_rank", header: "RS Rank", precision: 0, width: 95 },
      { field: "marketCap", header: "Mkt Cap", precision: 0, width: 130 },
    ],
    sort: [{ field: "rs_rank", dir: "asc" }],
  };
}

export function makeDemoWorkspace(): UserWorkspace {
  const workspace: UserWorkspace = {
    id: mintUlid(),
    schemaVersion: 1,
    name: "Demo Workspace",
    isActive: true,
    appId: "web",
    // The selected theme is workspace data (D13). Named explicitly rather than
    // left to the default, so the demo shows where a theme choice is persisted
    // — right-click → Settings → Theme writes this same field.
    themeRef: QUFOLIO_CLASSIC_THEME.id,
    layouts: [
      {
        id: mintUlid(),
        name: "Default",
        // 1 row × 2 columns: the chart takes the wide left cell, a screener
        // placeholder the narrow right cell. Sizes are `fr` so the resize
        // handle between them can redistribute space (see SectionGrid).
        rows: 1,
        columns: 2,
        rowSizes: ["1fr"],
        columnSizes: ["3fr", "1fr"],
        sections: [
          {
            id: mintUlid(),
            kind: "chartGroup",
            row: 0,
            column: 0,
            rowSpan: 1,
            columnSpan: 1,
            title: "DEMO Daily",
            config: newChartGroup("AAPL"),
          },
          {
            id: mintUlid(),
            kind: "screener",
            row: 0,
            column: 1,
            rowSpan: 1,
            columnSpan: 1,
            title: "Screener",
            config: screenerConfig(),
          },
        ],
      },
      {
        // A second layout, so the multi-layout and multi-chart paths are
        // reachable at all. Nothing renders more than one layout today —
        // `App` reads `layouts[0]` — which is exactly why a fixture with two of
        // them is worth having: the gap is invisible while the demo has one.
        id: mintUlid(),
        name: "Two Charts",
        rows: 1,
        columns: 2,
        rowSizes: ["1fr"],
        // Even, unlike Default's 3:1 — neither chart is the subordinate one.
        columnSizes: ["1fr", "1fr"],
        sections: [
          {
            id: mintUlid(),
            kind: "chartGroup",
            row: 0,
            column: 0,
            rowSpan: 1,
            columnSpan: 1,
            title: "AAPL Daily",
            config: newChartGroup("AAPL"),
          },
          {
            id: mintUlid(),
            kind: "chartGroup",
            row: 0,
            column: 1,
            rowSpan: 1,
            columnSpan: 1,
            title: "MSFT Daily",
            config: newChartGroup("MSFT"),
          },
        ],
      },
    ],
  };

  // Sanity-check the fixture against the contract schema (dev aid).
  const result = validateWorkspace(workspace);
  if (!result.valid) {
    // eslint-disable-next-line no-console
    console.error("[demo] workspace failed schema validation:", result.errors);
  }

  return workspace;
}